<template>
  <AdminLayout>
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-heading font-bold text-mifo-primary">Produkte</h1>
          <p class="mt-1 text-sm text-gray-500">Verwalten Sie Ihre Produkte und Add-ons</p>
        </div>
        <div class="flex space-x-3">
          <button 
            v-if="isReordering" 
            @click="saveOrder" 
            class="btn-secondary"
            :disabled="saving"
          >
            {{ saving ? 'Speichern...' : 'Sortierung speichern' }}
          </button>
          <button 
            v-if="isReordering" 
            @click="cancelReorder" 
            class="btn-outline"
          >
            Abbrechen
          </button>
          <button 
            v-if="!isReordering" 
            @click="startReorder" 
            class="btn-outline"
          >
            <GripVertical class="h-4 w-4 mr-2" />
            Sortierung ändern
          </button>
          <button class="btn-primary" @click="goCreate">Neues Produkt</button>
        </div>
      </div>

      <div class="bg-white shadow rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th v-if="isReordering" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preis</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktiv</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Add-on</th>
              <th class="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr 
              v-for="(p, index) in displayProducts" 
              :key="p.id" 
              class="hover:bg-gray-50 cursor-pointer" 
              :class="{ 'bg-blue-50': isReordering && draggedIndex === index }"
              @click="isReordering ? null : goDetail(p.id)"
              draggable="true"
              @dragstart="dragStart(index, $event)"
              @dragover.prevent
              @drop="drop(index, $event)"
            >
              <td v-if="isReordering" class="px-6 py-4 text-sm text-gray-400 select-none">
                <GripVertical class="h-5 w-5" />
              </td>
              <td class="px-6 py-4 text-sm text-gray-900">{{ p.name }}</td>
              <td class="px-6 py-4 text-sm text-gray-900">{{ formatCurrency(p.priceCents) }}</td>
              <td class="px-6 py-4 text-sm"><span :class="p.isActive ? 'text-green-700' : 'text-gray-400'">{{ p.isActive ? 'Ja' : 'Nein' }}</span></td>
              <td class="px-6 py-4 text-sm"><span :class="p.isAddonEnabled ? 'text-blue-700' : 'text-gray-400'">{{ p.isAddonEnabled ? 'Ja' : 'Nein' }}</span></td>
              <td class="px-6 py-4 text-right text-sm text-gray-400 select-none">›</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import api from '@/utils/axios'
import { formatCurrency } from 'shared/utils'
import { useRouter } from 'vue-router'
import { ref, computed, onMounted } from 'vue'
import AdminLayout from '@/components/Layout/AdminLayout.vue'
import { GripVertical } from 'lucide-vue-next'

export default {
  name: 'ProductList',
  components: { AdminLayout, GripVertical },
  setup() {
    const router = useRouter()
    const products = ref([])
    const isReordering = ref(false)
    const saving = ref(false)
    const draggedIndex = ref(null)
    const originalProducts = ref([])

    const displayProducts = computed(() => {
      return products.value
    })

    const load = async () => {
      const { data } = await api.get('/products/admin/all')
      products.value = data
    }

    const goCreate = () => router.push('/herzbeben/products/new')

    const goDetail = (id) => router.push(`/herzbeben/products/${id}`)

    const startReorder = () => {
      isReordering.value = true
      originalProducts.value = [...products.value]
    }

    const cancelReorder = () => {
      isReordering.value = false
      products.value = [...originalProducts.value]
      draggedIndex.value = null
    }

    const saveOrder = async () => {
      try {
        saving.value = true
        const productOrders = products.value.map((product, index) => ({
          id: product.id,
          sortOrder: index
        }))
        
        await api.put('/products/admin/sort-order', { productOrders })
        isReordering.value = false
        draggedIndex.value = null
        await load() // Reload to get updated data
      } catch (error) {
        console.error('Error saving sort order:', error)
        alert('Fehler beim Speichern der Sortierung')
      } finally {
        saving.value = false
      }
    }

    const dragStart = (index, event) => {
      if (!isReordering.value) return
      draggedIndex.value = index
      event.dataTransfer.effectAllowed = 'move'
    }

    const drop = (index, event) => {
      if (!isReordering.value || draggedIndex.value === null) return
      
      const draggedProduct = products.value[draggedIndex.value]
      const newProducts = [...products.value]
      
      // Remove dragged item
      newProducts.splice(draggedIndex.value, 1)
      
      // Insert at new position
      newProducts.splice(index, 0, draggedProduct)
      
      products.value = newProducts
      draggedIndex.value = null
    }

    onMounted(load)
    
    return { 
      products, 
      displayProducts,
      isReordering, 
      saving, 
      draggedIndex,
      formatCurrency, 
      goCreate, 
      goDetail,
      startReorder,
      cancelReorder,
      saveOrder,
      dragStart,
      drop
    }
  }
}
</script>

